import React, { PropsWithChildren } from 'react';

import { useDiscardPendingShareImport } from '@/feature/entry-share/hooks/use-share-link-import';
import { Product } from '@/hooks/use-jurisdiction';
import { LoadingScreen } from '@/screens/LoadingScreen';

import { PredictionGateStatus, usePredictionAccessGate } from '../hooks/use-prediction-access-gate';
import { PredictionUnavailableScreen } from './PredictionUnavailableScreen';

const TERMINAL_STATUSES: PredictionGateStatus[] = ['ineligible', 'pending', 'inReview'];

/**
 * Gates the Predictions behind a T&C acknowledgement + Polymarket KYC check.
 *
 * Renders `children` (the tab navigator) only once the user is eligible; otherwise shows a
 * loading state, drives the acknowledgement modal, or shows the "unavailable" screen. See
 * `usePredictionAccessGate` for the state machine.
 */
export const PredictionAccessGate = ({ children }: PropsWithChildren) => {
    const { status } = usePredictionAccessGate();
    useDiscardPendingShareImport(Product.Prediction, TERMINAL_STATUSES.includes(status));

    switch (status) {
        case 'checkingConfig':
        case 'needsTnc':
        case 'submittingKyc':
            return <LoadingScreen />;
        case 'pending':
            return <PredictionUnavailableScreen variant="pending" />;
        case 'ineligible':
        case 'inReview':
            return <PredictionUnavailableScreen />;
        case 'eligible':
        case 'inactive':
        default:
            return <>{children}</>;
    }
};
